import '../DisclosureAcknowledgement/DisclosureAcknowledgement.scss';

import { Button, Checkbox, FormControlLabel, Link } from '@huntington/honeycomb';
import ErrorIcon from '@mui/icons-material/Error';
import { Box, FormControl, FormHelperText, Typography } from '@mui/material';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAcceptTermsMutation } from '../../../api/user.api';
import CheckCircleIcon from '../../../assets/check-circle.svg?react';
import { DisclosureCancellationModal } from '../../../components/Modals/DisclosureCancellationModal/DisclosureCancellationModal';
import {
	DisclosureDocumentModal,
	type DisclosureType,
} from '../../../components/Modals/DisclosureDocumentModal/DisclosureDocumentModal';
import {
	ACKNOWLEDGEMENT_BOTTOM_CONSENT_LINK,
	ACKNOWLEDGEMENT_BOTTOM_ERROR_MESSAGE,
	ACKNOWLEDGEMENT_BOTTOM_SUBHEADER,
	ACKNOWLEDGEMENT_CANCEL_BUTTON,
	ACKNOWLEDGEMENT_DISCLOSURE_MESSAGE,
	ACKNOWLEDGEMENT_DISCLOSURE_SUBHEADER,
	ACKNOWLEDGEMENT_HEADER,
	ACKNOWLEDGEMENT_SUBMIT_BUTTON,
	ACKNOWLEDGEMENT_TOP_CONSENT_LINK,
	ACKNOWLEDGEMENT_TOP_ERROR_MESSAGE,
	ACKNOWLEDGEMENT_TOP_SUBHEADER,
} from '../../../constants/messages';
import { ROUTE_ENROLLMENT, ROUTE_ERROR, ROUTE_LOGIN, ROUTE_ROOT } from '../../../router/router';
import { ColorBaseGreenCompliant } from '@huntington/honeycomb-design';

export default function DisclosureAcknowledgement() {
	const [acceptTerms, { isLoading }] = useAcceptTermsMutation();

	// ---- DisclosureDocumentModal ---- (reusable)
	const [activeDoc, setActiveDoc] = useState<DisclosureType | null>(null);

	// ---- Checkbox state ----
	const [ackEConsent, setAckEConsent] = useState(false);
	const [ackTerms, setAckTerms] = useState(false);

	// "submitted" flag controls when errors show
	const [submitted, setSubmitted] = useState(false);

	// per-checkbox errors
	const eConsentError = submitted && !ackEConsent;
	const termsError = submitted && !ackTerms;

	// ---- DisclosureCancellationModal ----
	const [cancelOpen, setCancelOpen] = useState(false);
	const handleCancelOpen = () => setCancelOpen(true);
	const handleCancelClose = () => setCancelOpen(false);

	// if user confirms leaving
	const navigate = useNavigate();

	const handleLeave = () => {
		setCancelOpen(false);
		navigate(ROUTE_LOGIN);
	};

	// ---- Submit ----
	const handleSubmit = async () => {
		setSubmitted(true);

		if (!ackEConsent || !ackTerms) return;

		try {
			await acceptTerms().unwrap();
			navigate(`${ROUTE_ROOT}`);
		} catch {
			navigate(`${ROUTE_ENROLLMENT}/${ROUTE_ERROR}`);
		}
	};

	return (
		<>
			<Typography
				component="h1"
				variant="h1"
				sx={{ font: 'bold 2.25rem "Huntington Serif", serif', pb: '1.5rem' }}
			>
				{ACKNOWLEDGEMENT_HEADER}
			</Typography>

			<Box
				sx={{
					display: 'flex',
					flexDirection: 'column',
					alignItems: 'flex-start',
					gap: '2.5rem',
				}}
			>
				{/* ELECTRONIC RECORDS SECTION */}
				<Box className="ackSection">
					<Typography className="ackCardHeading">{ACKNOWLEDGEMENT_TOP_SUBHEADER}</Typography>

					<Box className="linkContainer">
						<CheckCircleIcon color={ColorBaseGreenCompliant} />
						<Link
							component="button"
							className="ackDocLink"
							onClick={() => setActiveDoc('ERECORDS')}
						>
							{ACKNOWLEDGEMENT_TOP_CONSENT_LINK}
						</Link>
					</Box>

					<Box
						sx={{
							display: 'flex',
							flexDirection: 'column',
							alignItems: 'flex-start',
							gap: 0.5,
							alignSelf: 'stretch',
						}}
					>
						<Typography className="ackSectionTitle">
							{ACKNOWLEDGEMENT_DISCLOSURE_SUBHEADER}
						</Typography>
						<FormControl error={eConsentError}>
							<FormControlLabel
								control={
									<Checkbox
										checked={ackEConsent}
										onChange={(e) => setAckEConsent(e.target.checked)}
									/>
								}
								label={
									<Typography sx={{ color: eConsentError ? 'error.main' : 'text.primary' }}>
										{ACKNOWLEDGEMENT_DISCLOSURE_MESSAGE}
									</Typography>
								}
							/>
							{eConsentError && (
								<FormHelperText
									style={{
										display: 'flex',
										alignItems: 'flex-start',
										gap: '0.625rem',
										color: '#394048',
										fontFamily: 'Muli',
										fontSize: '1rem',
										fontStyle: 'normal',
										fontWeight: '400',
										lineHeight: '1.5rem',
									}}
								>
									<ErrorIcon color="error" /> {ACKNOWLEDGEMENT_TOP_ERROR_MESSAGE}
								</FormHelperText>
							)}
						</FormControl>
					</Box>
				</Box>

				{/* TERMS & CONDITIONS SECTION */}
				<Box className="ackSection">
					<Typography className="ackCardHeading">{ACKNOWLEDGEMENT_BOTTOM_SUBHEADER}</Typography>

					<Box className="linkContainer">
						<CheckCircleIcon color={ColorBaseGreenCompliant} />
						<Link component="button" className="ackDocLink" onClick={() => setActiveDoc('TERMS')}>
							{ACKNOWLEDGEMENT_BOTTOM_CONSENT_LINK}
						</Link>
					</Box>

					<Box
						sx={{
							display: 'flex',
							flexDirection: 'column',
							alignItems: 'flex-start',
							gap: 0.5,
							alignSelf: 'stretch',
						}}
					>
						<Typography className="ackSectionTitle">
							{ACKNOWLEDGEMENT_DISCLOSURE_SUBHEADER}
						</Typography>
						<FormControl error={termsError}>
							<FormControlLabel
								control={
									<Checkbox checked={ackTerms} onChange={(e) => setAckTerms(e.target.checked)} />
								}
								label={
									<Typography sx={{ color: termsError ? 'error.main' : 'text.primary' }}>
										{ACKNOWLEDGEMENT_DISCLOSURE_MESSAGE}
									</Typography>
								}
							/>
							{termsError && (
								<FormHelperText
									style={{
										display: 'flex',
										alignItems: 'flex-start',
										gap: '0.625rem',
										color: '#394048',
										fontFamily: 'Muli',
										fontSize: '1rem',
										fontStyle: 'normal',
										fontWeight: '400',
										lineHeight: '1.5rem',
									}}
								>
									<ErrorIcon color="error" /> {ACKNOWLEDGEMENT_BOTTOM_ERROR_MESSAGE}
								</FormHelperText>
							)}
						</FormControl>
					</Box>
				</Box>
			</Box>

			{/* CANCEL / CONTINUE BUTTONS */}
			<Box className="ackActionsRow">
				<Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
					<Button
						className="ackContinueBtn"
						priority="secondary"
						variant="outlined"
						onClick={handleCancelOpen}
						disabled={isLoading}
					>
						{ACKNOWLEDGEMENT_CANCEL_BUTTON}
					</Button>
					<Button onClick={handleSubmit} disabled={isLoading}>
						{ACKNOWLEDGEMENT_SUBMIT_BUTTON}
					</Button>
				</Box>
			</Box>

			{/* USER CANCELLATION MODAL */}
			<DisclosureCancellationModal
				open={cancelOpen}
				onClose={handleCancelClose}
				onLeave={handleLeave}
			/>

			{/* DISCLOSURE DOCUMENT VIEWER MODAL */}
			{activeDoc !== null && (
				<DisclosureDocumentModal onClose={() => setActiveDoc(null)} type={activeDoc} />
			)}
		</>
	);
}
